import { zodResolver } from "@hookform/resolvers/zod";
import { createRootRoute, createRoute, createRouter, Link, Outlet, useNavigate } from "@tanstack/react-router";
import { Activity, AlertTriangle, AppWindow, Boxes, Bug, CheckCircle2, ChevronRight, ClipboardList, Cpu, HardDrive, LogOut, MessageSquare, Monitor, RefreshCw, Search, Server, Undo2 } from "lucide-react";
import { useEffect, useState, type FormEvent, type ReactNode } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { z } from "zod";

import { ApiError } from "@/api/client";
import type { AuditEvent, HostContainers, HostCorrelation, HostDisks, HostOverview, HostProcesses, Incident, IncidentComment, IncidentListInput, IngestionHealth, LogEntry, LogSearchInput, SignalFamilies, Station } from "@/api/types";
import {
  useAuditEvents,
  useApps,
  useClientIncidents,
  useClientStations,
  useDebugWindowMutations,
  useHostContainers,
  useHostCorrelation,
  useHostDisks,
  useHostOverview,
  useHostProcesses,
  useHostSignalFamilies,
  useIngestionHealth,
  useIncident,
  useIncidents,
  useIncidentMutations,
  useLogSearch
} from "@/api/query-hooks";
import { useAuth } from "@/auth/use-auth";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { EmptyState } from "@/components/ui/empty-state";
import { ago, dateTime } from "@/lib/date";

type IncidentInboxSearch = {
  clientId?: number;
  status: "" | "open" | "resolved";
  severity: "" | "debug" | "info" | "warn" | "error";
  appId?: string;
  ruleId?: string;
  limit: number;
};

type IncidentInboxDraft = {
  clientId: string;
  status: string;
  severity: string;
  appId: string;
  ruleId: string;
  limit: string;
};

const defaultIncidentInboxSearch: IncidentInboxSearch = {
  clientId: undefined,
  status: "open",
  severity: "",
  appId: undefined,
  ruleId: undefined,
  limit: 50
};

const defaultIncidentInboxDraft: IncidentInboxDraft = {
  clientId: "",
  status: "open",
  severity: "",
  appId: "",
  ruleId: "",
  limit: "50"
};

const rootRoute = createRootRoute({
  component: RootLayout
});

const indexRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/",
  component: DashboardPage
});

const appsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/apps",
  component: AppsPage
});

const ingestionRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/ingestion",
  component: IngestionPage
});

const auditRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/audit",
  component: AuditPage
});

const incidentInboxRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/incidents",
  validateSearch: normalizeIncidentInboxSearch,
  component: IncidentInboxPage
});

const clientRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/clients/$clientId",
  component: ClientPage
});

const hostRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/clients/$clientId/hosts/$hostId",
  component: HostPage
});

const incidentRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/incidents/$incidentId",
  component: IncidentPage
});

const routeTree = rootRoute.addChildren([indexRoute, appsRoute, ingestionRoute, auditRoute, incidentInboxRoute, clientRoute, hostRoute, incidentRoute]);

export const router = createRouter({ routeTree });

declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router;
  }
}

function RootLayout() {
  const auth = useAuth();
  const { t } = useTranslation(["common", "auth"]);
  if (auth.status === "loading") {
    return <FullPageState title={t("auth:loading")} />;
  }
  if (auth.status === "anonymous") {
    return <FullPageState title={t("common:brand")} action={<Button onClick={() => auth.login()}>{t("auth:logIn")}</Button>} />;
  }
  if (auth.status === "forbidden") {
    return <FullPageState title={t("auth:accessDenied")} action={<Button onClick={auth.logout}>{t("auth:logOut")}</Button>} />;
  }
  if (auth.status === "unavailable") {
    return <FullPageState title={t("auth:authenticationUnavailable")} action={<Button onClick={() => auth.login()}>{t("auth:retry")}</Button>} />;
  }
  return (
    <div className="min-h-screen">
      <header className="border-b bg-panel">
        <div className="mx-auto flex max-w-7xl items-center justify-between px-4 py-3">
          <Link to="/" className="flex items-center gap-2 text-sm font-semibold">
            <Bug className="h-5 w-5 text-primary" />
            {t("common:brand")}
          </Link>
          <nav className="flex items-center gap-1">
            <NavLink to="/incidents" icon={<AlertTriangle className="h-4 w-4" />} label={t("common:nav.incidents")} />
            <NavLink to="/apps" icon={<AppWindow className="h-4 w-4" />} label={t("common:nav.apps")} />
            <NavLink to="/ingestion" icon={<Activity className="h-4 w-4" />} label={t("common:nav.ingestion")} />
            <NavLink to="/audit" icon={<ClipboardList className="h-4 w-4" />} label={t("common:nav.audit")} />
            <span className="hidden px-3 text-xs text-muted-foreground md:inline">{auth.userName}</span>
            <Button variant="ghost" onClick={auth.logout} title={t("auth:logOut")}>
              <LogOut className="h-4 w-4" />
            </Button>
          </nav>
        </div>
      </header>
      <main className="mx-auto max-w-7xl px-4 py-6">
        <Outlet />
      </main>
    </div>
  );
}

function FullPageState({ title, action }: { title: string; action?: ReactNode }) {
  return (
    <div className="flex min-h-screen items-center justify-center px-4">
      <div className="w-full max-w-sm rounded-md border bg-panel p-6 text-center">
        <h1 className="text-lg font-semibold">{title}</h1>
        {action ? <div className="mt-4">{action}</div> : null}
      </div>
    </div>
  );
}

function NavLink({ to, icon, label }: { to: "/incidents" | "/apps" | "/ingestion" | "/audit"; icon: ReactNode; label: string }) {
  return (
    <Link
      to={to}
      className="inline-flex h-9 items-center gap-2 rounded-md px-3 text-sm text-muted-foreground hover:bg-accent hover:text-foreground"
      activeProps={{ className: "bg-accent text-foreground" }}
    >
      {icon}
      {label}
    </Link>
  );
}

function DashboardPage() {
  const navigate = useNavigate();
  const { t } = useTranslation("common");
  const [clientId, setClientId] = useState("42");
  const submit = (event: FormEvent) => {
    event.preventDefault();
    const parsed = Number(clientId);
    if (Number.isInteger(parsed) && parsed > 0) {
      void navigate({ to: "/clients/$clientId", params: { clientId: String(parsed) } });
    }
  };
  return (
    <section className="space-y-5">
      <PageTitle title={t("pages.operations")} />
      <form className="flex max-w-md gap-2" onSubmit={submit}>
        <input
          className="h-9 min-w-0 flex-1 rounded-md border bg-input px-3 text-sm outline-none focus:ring-2 focus:ring-ring"
          inputMode="numeric"
          value={clientId}
          onChange={(event) => setClientId(event.target.value)}
          aria-label={t("fields.client")}
        />
        <Button type="submit" variant="primary">
          <Search className="h-4 w-4" />
          {t("actions.open")}
        </Button>
      </form>
    </section>
  );
}

function normalizeIncidentInboxSearch(search: Record<string, unknown>): IncidentInboxSearch {
  return {
    clientId: positiveIntSearchValue(search.clientId),
    status: incidentStatusSearchValue(search.status),
    severity: incidentSeveritySearchValue(search.severity),
    appId: stringSearchValue(search.appId),
    ruleId: stringSearchValue(search.ruleId),
    limit: incidentLimitSearchValue(search.limit)
  };
}

function draftFromIncidentInboxSearch(search: IncidentInboxSearch): IncidentInboxDraft {
  return {
    clientId: search.clientId ? String(search.clientId) : "",
    status: search.status,
    severity: search.severity,
    appId: search.appId ?? "",
    ruleId: search.ruleId ?? "",
    limit: String(search.limit)
  };
}

function compactIncidentInboxSearch(draft: IncidentInboxDraft): IncidentInboxSearch {
  const clientId = positiveIntSearchValue(draft.clientId);
  return {
    clientId,
    status: incidentStatusSearchValue(draft.status),
    severity: incidentSeveritySearchValue(draft.severity),
    appId: draft.appId.trim() || undefined,
    ruleId: draft.ruleId.trim() || undefined,
    limit: incidentLimitSearchValue(draft.limit)
  };
}

function incidentListInputFromSearch(search: IncidentInboxSearch): IncidentListInput {
  const input: IncidentListInput = {
    status: search.status || undefined,
    severity: search.severity || undefined,
    appId: search.appId?.trim() || undefined,
    ruleId: search.ruleId?.trim() || undefined,
    limit: search.limit
  };
  if (search.clientId) {
    input.clientId = search.clientId;
  }
  return input;
}

function positiveIntSearchValue(value: unknown) {
  const normalized = typeof value === "string" ? unquoteSearchString(value) : value;
  const numeric = typeof normalized === "number" ? normalized : typeof normalized === "string" ? Number(normalized) : 0;
  return Number.isInteger(numeric) && numeric > 0 ? numeric : undefined;
}

function stringSearchValue(value: unknown) {
  if (typeof value === "number" && Number.isFinite(value)) {
    return String(value);
  }
  if (typeof value !== "string") {
    return "";
  }
  return unquoteSearchString(value);
}

function incidentStatusSearchValue(value: unknown): IncidentInboxSearch["status"] {
  const normalized = typeof value === "string" ? unquoteSearchString(value) : value;
  return normalized === "open" || normalized === "resolved" || normalized === "" ? normalized : defaultIncidentInboxSearch.status;
}

function incidentSeveritySearchValue(value: unknown): IncidentInboxSearch["severity"] {
  const normalized = typeof value === "string" ? unquoteSearchString(value) : value;
  return normalized === "debug" || normalized === "info" || normalized === "warn" || normalized === "error" || normalized === "" ? normalized : "";
}

function incidentLimitSearchValue(value: unknown): IncidentInboxSearch["limit"] {
  const normalized = typeof value === "string" ? unquoteSearchString(value) : value;
  const numeric = typeof normalized === "number" ? normalized : typeof normalized === "string" ? Number(normalized) : 0;
  return numeric === 25 || numeric === 50 || numeric === 100 || numeric === 200 ? numeric : defaultIncidentInboxSearch.limit;
}

function unquoteSearchString(value: string) {
  if (value.length >= 2 && value.startsWith('"') && value.endsWith('"')) {
    try {
      const parsed: unknown = JSON.parse(value);
      return typeof parsed === "string" ? parsed : value;
    } catch {
      return value;
    }
  }
  return value;
}

function AppsPage() {
  const { t } = useTranslation(["apps", "common"]);
  const apps = useApps();
  return (
    <section className="space-y-5">
      <PageTitle title={t("apps:title")} action={<RefreshButton onClick={() => void apps.refetch()} />} />
      <DataState error={apps.error} loading={apps.isLoading}>
        <div className="overflow-hidden rounded-md border bg-panel">
          <table className="w-full table-fixed text-left text-sm">
            <thead className="bg-muted text-xs uppercase text-muted-foreground">
              <tr>
                <th className="w-1/4 px-3 py-2">{t("common:fields.id")}</th>
                <th className="px-3 py-2">{t("common:fields.name")}</th>
                <th className="w-48 px-3 py-2">{t("common:fields.updated")}</th>
              </tr>
            </thead>
            <tbody>
              {apps.data?.entries.map((app) => (
                <tr key={app.id} className="border-t">
                  <td className="truncate px-3 py-2 font-medium">{app.id}</td>
                  <td className="truncate px-3 py-2">{app.name}</td>
                  <td className="px-3 py-2 text-muted-foreground">{dateTime(app.updatedAt)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </DataState>
    </section>
  );
}

function IngestionPage() {
  const { t } = useTranslation("common");
  const health = useIngestionHealth();
  return (
    <section className="space-y-5">
      <PageTitle title={t("pages.ingestion")} action={<RefreshButton onClick={() => void health.refetch()} />} />
      <DataState error={health.error} loading={health.isLoading}>
        {health.data ? <IngestionHealthPanel data={health.data} /> : <EmptyState title={t("state.none")} />}
      </DataState>
    </section>
  );
}

function AuditPage() {
  const { t } = useTranslation("audit");
  const audit = useAuditEvents();
  return (
    <section className="space-y-5">
      <PageTitle title={t("title")} action={<RefreshButton onClick={() => void audit.refetch()} />} />
      <DataState error={audit.error} loading={audit.isLoading}>
        <AuditEventsTable events={audit.data?.entries ?? []} />
      </DataState>
    </section>
  );
}

function IncidentInboxPage() {
  const { t } = useTranslation(["incidents", "common"]);
  const search = incidentInboxRoute.useSearch();
  const navigate = useNavigate();
  const [draft, setDraft] = useState<IncidentInboxDraft>(() => draftFromIncidentInboxSearch(search));
  const incidents = useIncidents(incidentListInputFromSearch(search));
  const scopeRequired = incidents.error instanceof ApiError && incidents.error.code === "client_scope_required";

  useEffect(() => {
    setDraft(draftFromIncidentInboxSearch(search));
  }, [search.clientId, search.status, search.severity, search.appId, search.ruleId, search.limit]);

  const updateDraft = (field: keyof IncidentInboxDraft, value: string) => {
    setDraft((current) => ({ ...current, [field]: value }));
  };
  const submit = (event: FormEvent) => {
    event.preventDefault();
    void navigate({ to: "/incidents", search: compactIncidentInboxSearch(draft) });
  };
  const reset = () => {
    setDraft(defaultIncidentInboxDraft);
    void navigate({ to: "/incidents", search: compactIncidentInboxSearch(defaultIncidentInboxDraft) });
  };

  return (
    <section className="space-y-5">
      <PageTitle title={t("incidents:inboxTitle")} action={<RefreshButton onClick={() => void incidents.refetch()} />} />
      <Panel title={t("incidents:filters")}>
        <form className="grid gap-3 md:grid-cols-2 xl:grid-cols-6" onSubmit={submit}>
          <IncidentInboxInput label={t("common:fields.client")} value={draft.clientId} onChange={(value) => updateDraft("clientId", value)} placeholder="42" inputMode="numeric" />
          <label className="space-y-1 text-xs uppercase text-muted-foreground">
            <span>{t("common:fields.status")}</span>
            <select
              className="h-9 w-full rounded-md border bg-input px-2 text-sm normal-case text-foreground outline-none focus:ring-2 focus:ring-ring"
              value={draft.status}
              onChange={(event) => updateDraft("status", event.target.value)}
              aria-label={t("common:fields.status")}
            >
              <option value="">{t("common:state.any")}</option>
              <option value="open">open</option>
              <option value="resolved">resolved</option>
            </select>
          </label>
          <label className="space-y-1 text-xs uppercase text-muted-foreground">
            <span>{t("common:fields.severity")}</span>
            <select
              className="h-9 w-full rounded-md border bg-input px-2 text-sm normal-case text-foreground outline-none focus:ring-2 focus:ring-ring"
              value={draft.severity}
              onChange={(event) => updateDraft("severity", event.target.value)}
              aria-label={t("common:fields.severity")}
            >
              <option value="">{t("common:state.any")}</option>
              <option value="debug">debug</option>
              <option value="info">info</option>
              <option value="warn">warn</option>
              <option value="error">error</option>
            </select>
          </label>
          <IncidentInboxInput label={t("common:fields.app")} value={draft.appId} onChange={(value) => updateDraft("appId", value)} placeholder="mailarchiver" />
          <IncidentInboxInput label={t("incidents:rule")} value={draft.ruleId} onChange={(value) => updateDraft("ruleId", value)} placeholder="mailarchiver.backup_failed.repeated" />
          <label className="space-y-1 text-xs uppercase text-muted-foreground">
            <span>{t("common:fields.limit")}</span>
            <select
              className="h-9 w-full rounded-md border bg-input px-2 text-sm normal-case text-foreground outline-none focus:ring-2 focus:ring-ring"
              value={draft.limit}
              onChange={(event) => updateDraft("limit", event.target.value)}
              aria-label={t("common:fields.limit")}
            >
              <option value="25">25</option>
              <option value="50">50</option>
              <option value="100">100</option>
              <option value="200">200</option>
            </select>
          </label>
          <div className="flex flex-wrap items-end gap-2 md:col-span-2 xl:col-span-6">
            <Button type="submit" variant="primary">
              <Search className="h-4 w-4" />
              {t("common:actions.apply")}
            </Button>
            <Button type="button" variant="ghost" onClick={reset}>
              {t("common:actions.reset")}
            </Button>
          </div>
        </form>
      </Panel>
      <Panel title={t("incidents:queue")}>
        {scopeRequired ? (
          <EmptyState title={t("incidents:scopeRequired")} />
        ) : (
          <DataState error={incidents.error} loading={incidents.isLoading}>
            <IncidentInboxTable incidents={incidents.data?.entries ?? []} />
          </DataState>
        )}
      </Panel>
    </section>
  );
}

function ClientPage() {
  const { t } = useTranslation(["common", "stations", "incidents"]);
  const { clientId } = clientRoute.useParams();
  const id = Number(clientId);
  const stations = useClientStations(id);
  const incidents = useClientIncidents(id);
  return (
    <section className="space-y-6">
      <PageTitle title={`Client ${id}`} action={<RefreshButton onClick={() => void Promise.all([stations.refetch(), incidents.refetch()])} />} />
      <div className="grid gap-6 xl:grid-cols-[minmax(0,1.2fr)_minmax(360px,0.8fr)]">
        <Panel title={t("stations:title")}>
          <DataState error={stations.error} loading={stations.isLoading}>
            <StationTable stations={stations.data?.entries ?? []} />
          </DataState>
        </Panel>
        <Panel title={t("incidents:title")}>
          <DataState error={incidents.error} loading={incidents.isLoading}>
            <IncidentList incidents={incidents.data?.entries ?? []} />
          </DataState>
        </Panel>
      </div>
      <Panel title={t("incidents:logs")}>
        <LogSearchPanel scope={{ clientId: id }} />
      </Panel>
    </section>
  );
}

function HostPage() {
  const { clientId, hostId } = hostRoute.useParams();
  const id = Number(clientId);
  const overview = useHostOverview(id, hostId);
  const signalFamilies = useHostSignalFamilies(id, hostId);
  const disks = useHostDisks(id, hostId);
  const processes = useHostProcesses(id, hostId);
  const containers = useHostContainers(id, hostId);
  const correlation = useHostCorrelation(id, hostId);
  const refresh = () =>
    void Promise.all([
      overview.refetch(),
      signalFamilies.refetch(),
      disks.refetch(),
      processes.refetch(),
      containers.refetch(),
      correlation.refetch()
    ]);

  return (
    <section className="space-y-6">
      <div className="space-y-3">
        <Link to="/clients/$clientId" params={{ clientId: String(id) }} className="inline-flex items-center text-sm text-muted-foreground hover:text-foreground">
          Client {id}
        </Link>
        <PageTitle title={hostId} action={<RefreshButton onClick={refresh} />} />
      </div>

      <div className="grid gap-6 xl:grid-cols-[minmax(0,1fr)_minmax(360px,0.72fr)]">
        <Panel title="Overview">
          <DataState error={overview.error} loading={overview.isLoading}>
            {overview.data ? <HostOverviewPanel data={overview.data} /> : <EmptyState title="No host overview" />}
          </DataState>
        </Panel>
        <Panel title="Signal Families">
          <DataState error={signalFamilies.error} loading={signalFamilies.isLoading}>
            {signalFamilies.data ? <SignalFamiliesPanel data={signalFamilies.data} /> : <EmptyState title="No signal families" />}
          </DataState>
        </Panel>
      </div>

      <div className="grid gap-6 xl:grid-cols-2">
        <Panel title="Disks And SMART">
          <DataState error={disks.error} loading={disks.isLoading}>
            {disks.data ? <DisksPanel data={disks.data} /> : <EmptyState title="No disk data" />}
          </DataState>
        </Panel>
        <Panel title="Process Pressure">
          <DataState error={processes.error} loading={processes.isLoading}>
            {processes.data ? <ProcessesPanel data={processes.data} /> : <EmptyState title="No process data" />}
          </DataState>
        </Panel>
      </div>

      <div className="grid gap-6 xl:grid-cols-2">
        <Panel title="Containers">
          <DataState error={containers.error} loading={containers.isLoading}>
            {containers.data ? <ContainersPanel data={containers.data} /> : <EmptyState title="No container data" />}
          </DataState>
        </Panel>
        <Panel title="Correlation">
          <DataState error={correlation.error} loading={correlation.isLoading}>
            {correlation.data ? <CorrelationPanel data={correlation.data} /> : <EmptyState title="No correlated logs" />}
          </DataState>
        </Panel>
      </div>

      <Panel title="Logs">
        <LogSearchPanel scope={{ clientId: id, stationId: hostId }} />
      </Panel>
    </section>
  );
}

function IncidentPage() {
  const { t } = useTranslation("incidents");
  const { incidentId } = incidentRoute.useParams();
  const incident = useIncident(incidentId);
  return (
    <section className="space-y-5">
      <PageTitle title={t("detailTitle")} action={<RefreshButton onClick={() => void incident.refetch()} />} />
      <DataState error={incident.error} loading={incident.isLoading}>
        {incident.data ? <IncidentDetail incident={incident.data} /> : <EmptyState title={t("notFound")} />}
      </DataState>
    </section>
  );
}

function StationTable({ stations }: { stations: Station[] }) {
  const { t } = useTranslation(["stations", "common"]);
  if (stations.length === 0) {
    return <EmptyState title={t("stations:empty")} />;
  }
  return (
    <>
      <div className="space-y-3 md:hidden">
        {stations.map((station) => (
          <div key={station.stationId} className="rounded-md border p-3">
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <Link
                  to="/clients/$clientId/hosts/$hostId"
                  params={{ clientId: String(station.clientId), hostId: station.stationId }}
                  className="block truncate font-medium hover:text-primary"
                >
                  {station.hostname || station.stationId}
                </Link>
                <div className="truncate text-xs text-muted-foreground">{station.stationId}</div>
              </div>
              <Badge tone={station.licenseStatus === "ok" ? "success" : "warning"}>{station.licenseStatus || "unknown"}</Badge>
            </div>
            <div className="mt-3 grid grid-cols-2 gap-3 text-sm">
              <Field label={t("common:fields.app")} value={station.appId} />
              <Field label={t("common:fields.version")} value={station.softwareVersion} />
              <Field label={t("common:fields.platform")} value={`${station.os}/${station.arch}`} />
              <Field label={t("common:fields.lastSeen")} value={ago(station.lastSeenAt)} />
            </div>
            <div className="mt-3">
              <StationDebugWindow stationId={station.stationId} clientId={station.clientId} compact />
            </div>
          </div>
        ))}
      </div>
      <div className="hidden overflow-x-auto md:block">
        <table className="w-full min-w-[760px] text-left text-sm">
          <thead className="bg-muted text-xs uppercase text-muted-foreground">
            <tr>
              <th className="px-3 py-2">{t("common:fields.station")}</th>
              <th className="px-3 py-2">{t("common:fields.app")}</th>
              <th className="px-3 py-2">{t("common:fields.version")}</th>
              <th className="px-3 py-2">{t("common:fields.license")}</th>
              <th className="px-3 py-2">{t("common:fields.lastSeen")}</th>
              <th className="px-3 py-2"></th>
            </tr>
          </thead>
          <tbody>
            {stations.map((station) => (
              <tr key={station.stationId} className="border-t align-top">
                <td className="px-3 py-2">
                  <Link
                    to="/clients/$clientId/hosts/$hostId"
                    params={{ clientId: String(station.clientId), hostId: station.stationId }}
                    className="block font-medium hover:text-primary"
                  >
                    {station.hostname || station.stationId}
                  </Link>
                  <div className="truncate text-xs text-muted-foreground">{station.stationId}</div>
                </td>
                <td className="px-3 py-2">{station.appId}</td>
                <td className="px-3 py-2">
                  <div>{station.softwareVersion}</div>
                  <div className="text-xs text-muted-foreground">
                    {station.os}/{station.arch}
                  </div>
                </td>
                <td className="px-3 py-2">
                  <Badge tone={station.licenseStatus === "ok" ? "success" : "warning"}>{station.licenseStatus || "unknown"}</Badge>
                </td>
                <td className="px-3 py-2 text-muted-foreground">{ago(station.lastSeenAt)}</td>
                <td className="px-3 py-2">
                  <div className="flex flex-wrap gap-2">
                    <Link
                      to="/clients/$clientId/hosts/$hostId"
                      params={{ clientId: String(station.clientId), hostId: station.stationId }}
                      className="inline-flex h-9 items-center justify-center rounded-md border px-3 text-sm font-medium hover:bg-accent"
                    >
                      {t("common:actions.open")}
                    </Link>
                    <StationDebugWindow stationId={station.stationId} clientId={station.clientId} compact />
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  );
}

function IncidentList({ incidents }: { incidents: Incident[] }) {
  const { t } = useTranslation("incidents");
  if (incidents.length === 0) {
    return <EmptyState title={t("empty")} />;
  }
  return (
    <div className="divide-y rounded-md border">
      {incidents.map((incident) => (
        <Link key={incident.id} to="/incidents/$incidentId" params={{ incidentId: incident.id }} className="flex items-start justify-between gap-3 p-3 hover:bg-accent">
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              <Badge tone={incident.severity === "error" ? "danger" : "warning"}>{incident.severity}</Badge>
              <span className="truncate text-sm font-medium">{incident.eventName}</span>
            </div>
            <div className="mt-1 truncate text-sm text-muted-foreground">{incident.summary}</div>
            <div className="mt-1 text-xs text-muted-foreground">{ago(incident.lastSeenAt)}</div>
          </div>
          <ChevronRight className="mt-1 h-4 w-4 shrink-0 text-muted-foreground" />
        </Link>
      ))}
    </div>
  );
}

function IncidentInboxInput({
  label,
  value,
  onChange,
  placeholder,
  inputMode
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  placeholder: string;
  inputMode?: "numeric";
}) {
  return (
    <label className="space-y-1 text-xs uppercase text-muted-foreground">
      <span>{label}</span>
      <input
        className="h-9 w-full rounded-md border bg-input px-3 text-sm normal-case text-foreground outline-none focus:ring-2 focus:ring-ring"
        value={value}
        onChange={(event) => onChange(event.target.value)}
        placeholder={placeholder}
        inputMode={inputMode}
        aria-label={label}
      />
    </label>
  );
}

function IncidentInboxTable({ incidents }: { incidents: Incident[] }) {
  const { t } = useTranslation(["incidents", "common"]);
  if (incidents.length === 0) {
    return <EmptyState title={t("incidents:empty")} />;
  }
  return (
    <>
      <div className="space-y-3 lg:hidden">
        {incidents.map((incident) => (
          <div key={incident.id} className="rounded-md border p-3" data-testid={`incident-card-${incident.id}`}>
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <div className="flex flex-wrap items-center gap-2">
                  <Badge tone={incidentStatusTone(incident.status)}>{incident.status}</Badge>
                  <Badge tone={incidentSeverityTone(incident.severity)}>{incident.severity}</Badge>
                </div>
                <Link to="/incidents/$incidentId" params={{ incidentId: incident.id }} className="mt-2 block truncate font-medium hover:text-primary">
                  {incident.eventName}
                </Link>
                <div className="mt-1 line-clamp-2 text-sm text-muted-foreground">{incident.summary}</div>
              </div>
              <ChevronRight className="mt-1 h-4 w-4 shrink-0 text-muted-foreground" />
            </div>
            <div className="mt-3 grid grid-cols-2 gap-3 text-sm">
              <Field label={t("common:fields.client")} value={String(incident.clientId)} />
              <Field label={t("common:fields.app")} value={incident.appId} />
              <Field label={t("common:fields.station")} value={incident.stationId || ""} />
              <Field label={t("incidents:lastSeen")} value={ago(incident.lastSeenAt)} />
            </div>
            <div className="mt-3 flex flex-wrap gap-2">
              <Link to="/clients/$clientId" params={{ clientId: String(incident.clientId) }} className="inline-flex h-9 items-center justify-center rounded-md border px-3 text-sm font-medium hover:bg-accent">
                {t("incidents:openClient")}
              </Link>
              {incident.stationId ? (
                <Link
                  to="/clients/$clientId/hosts/$hostId"
                  params={{ clientId: String(incident.clientId), hostId: incident.stationId }}
                  className="inline-flex h-9 items-center justify-center rounded-md border px-3 text-sm font-medium hover:bg-accent"
                >
                  {t("incidents:openHost")}
                </Link>
              ) : null}
            </div>
            <IncidentQuickActions incident={incident} />
          </div>
        ))}
      </div>
      <div className="hidden overflow-x-auto lg:block">
        <table className="w-full min-w-[1080px] text-left text-sm">
          <thead className="bg-muted text-xs uppercase text-muted-foreground">
            <tr>
              <th className="px-3 py-2">{t("incidents:incident")}</th>
              <th className="px-3 py-2">{t("common:fields.scope")}</th>
              <th className="px-3 py-2">{t("incidents:lastSeen")}</th>
              <th className="px-3 py-2">{t("incidents:countShort")}</th>
              <th className="px-3 py-2">{t("incidents:actions")}</th>
            </tr>
          </thead>
          <tbody>
            {incidents.map((incident) => (
              <tr key={incident.id} className="border-t align-top" data-testid={`incident-row-${incident.id}`}>
                <td className="max-w-[420px] px-3 py-3">
                  <div className="flex flex-wrap items-center gap-2">
                    <Badge tone={incidentStatusTone(incident.status)}>{incident.status}</Badge>
                    <Badge tone={incidentSeverityTone(incident.severity)}>{incident.severity}</Badge>
                    {incident.ruleId ? <span className="truncate text-xs text-muted-foreground">{incident.ruleId}</span> : null}
                  </div>
                  <Link to="/incidents/$incidentId" params={{ incidentId: incident.id }} className="mt-2 block truncate font-medium hover:text-primary">
                    {incident.eventName}
                  </Link>
                  <div className="mt-1 line-clamp-2 text-sm text-muted-foreground">{incident.summary}</div>
                </td>
                <td className="px-3 py-3">
                  <div className="space-y-1">
                    <Link to="/clients/$clientId" params={{ clientId: String(incident.clientId) }} className="block font-medium hover:text-primary">
                      client {incident.clientId}
                    </Link>
                    {incident.stationId ? (
                      <Link
                        to="/clients/$clientId/hosts/$hostId"
                        params={{ clientId: String(incident.clientId), hostId: incident.stationId }}
                        className="block max-w-[260px] truncate text-xs text-muted-foreground hover:text-foreground"
                      >
                        {incident.stationId}
                      </Link>
                    ) : null}
                    <div className="text-xs text-muted-foreground">{incident.appId}</div>
                  </div>
                </td>
                <td className="whitespace-nowrap px-3 py-3 text-muted-foreground">
                  <div>{ago(incident.lastSeenAt)}</div>
                  <div className="text-xs">{dateTime(incident.lastSeenAt)}</div>
                </td>
                <td className="px-3 py-3">{incident.count}</td>
                <td className="min-w-[280px] px-3 py-3">
                  <IncidentQuickActions incident={incident} />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  );
}

function IncidentQuickActions({ incident }: { incident: Incident }) {
  const { t } = useTranslation("incidents");
  const mutations = useIncidentMutations(incident.id, incident.clientId);
  const [note, setNote] = useState("");
  const busy = mutations.acknowledge.isPending || mutations.resolve.isPending;
  const error = mutations.acknowledge.error || mutations.resolve.error;
  const canAcknowledge = incident.status === "open" && !incident.acknowledgedAt;
  const canResolve = incident.status === "open";
  const resolve = (event: FormEvent) => {
    event.preventDefault();
    void mutations.resolve.mutateAsync(note.trim()).then(() => setNote(""));
  };
  if (!canAcknowledge && !canResolve) {
    return (
      <div className="mt-3 flex flex-wrap items-center gap-2 lg:mt-0">
        <Link to="/incidents/$incidentId" params={{ incidentId: incident.id }} className="inline-flex h-9 items-center justify-center rounded-md border px-3 text-sm font-medium hover:bg-accent">
          {t("openDetail")}
        </Link>
      </div>
    );
  }
  return (
    <div className="mt-3 space-y-2 lg:mt-0">
      <div className="flex flex-wrap gap-2">
        {canAcknowledge ? (
          <Button type="button" variant="secondary" disabled={busy} onClick={() => void mutations.acknowledge.mutateAsync()}>
            <CheckCircle2 className="h-4 w-4" />
            {t("acknowledge")}
          </Button>
        ) : null}
        <Link to="/incidents/$incidentId" params={{ incidentId: incident.id }} className="inline-flex h-9 items-center justify-center rounded-md border px-3 text-sm font-medium hover:bg-accent">
          {t("openDetail")}
        </Link>
      </div>
      {canResolve ? (
        <form className="flex min-w-0 gap-2" onSubmit={resolve}>
          <input
            className="h-9 min-w-0 flex-1 rounded-md border bg-input px-3 text-sm outline-none focus:ring-2 focus:ring-ring"
            value={note}
            onChange={(event) => setNote(event.target.value)}
            placeholder={t("resolutionPlaceholder")}
            aria-label={t("resolutionNote")}
            maxLength={1000}
          />
          <Button type="submit" variant="primary" disabled={busy}>
            {t("resolve")}
          </Button>
        </form>
      ) : null}
      {error ? <div className="text-xs text-destructive">{(error as ApiError).message}</div> : null}
    </div>
  );
}

function IncidentDetail({ incident }: { incident: Incident }) {
  const { t } = useTranslation(["incidents", "common"]);
  const mutations = useIncidentMutations(incident.id, incident.clientId);
  const [resolutionNote, setResolutionNote] = useState("");
  const [commentBody, setCommentBody] = useState("");
  const busy = mutations.acknowledge.isPending || mutations.resolve.isPending || mutations.reopen.isPending || mutations.comment.isPending;
  const actionError = mutations.acknowledge.error || mutations.resolve.error || mutations.reopen.error || mutations.comment.error;
  const submitResolve = (event: FormEvent) => {
    event.preventDefault();
    void mutations.resolve.mutateAsync(resolutionNote).then(() => setResolutionNote(""));
  };
  const submitComment = (event: FormEvent) => {
    event.preventDefault();
    const body = commentBody.trim();
    if (!body) {
      return;
    }
    void mutations.comment.mutateAsync(body).then(() => setCommentBody(""));
  };
  const comments = incident.comments ?? [];
  return (
    <div className="space-y-4 rounded-md border bg-panel p-4">
      <div>
        <div className="flex flex-wrap items-center gap-2">
          <Badge tone={incident.status === "open" ? "danger" : "success"}>{incident.status}</Badge>
          <Badge tone={incident.severity === "error" ? "danger" : "warning"}>{incident.severity}</Badge>
          <span className="text-sm text-muted-foreground">{t("incidents:count", { count: incident.count })}</span>
        </div>
        <h2 className="mt-3 text-xl font-semibold">{incident.eventName}</h2>
        <p className="mt-2 text-sm text-muted-foreground">{incident.summary}</p>
      </div>
      <dl className="grid gap-3 text-sm sm:grid-cols-2 lg:grid-cols-3">
        <Field label={t("common:fields.client")} value={String(incident.clientId)} />
        <Field label={t("common:fields.station")} value={incident.stationId || ""} />
        <Field label={t("common:fields.app")} value={incident.appId} />
        <Field label={t("incidents:firstSeen")} value={dateTime(incident.firstSeenAt)} />
        <Field label={t("incidents:lastSeen")} value={dateTime(incident.lastSeenAt)} />
        <Field label={t("incidents:dedupe")} value={incident.dedupeKey} />
        <Field label={t("incidents:acknowledgedBy")} value={incident.acknowledgedBy || ""} />
        <Field label={t("incidents:acknowledgedAt")} value={incident.acknowledgedAt ? dateTime(incident.acknowledgedAt) : ""} />
        <Field label={t("incidents:resolvedBy")} value={incident.resolvedBy || ""} />
        <Field label={t("incidents:resolvedAt")} value={incident.resolvedAt ? dateTime(incident.resolvedAt) : ""} />
      </dl>
      {incident.resolutionNote ? (
        <div className="rounded-md border bg-muted p-3 text-sm">
          <div className="text-xs uppercase text-muted-foreground">{t("incidents:resolutionNote")}</div>
          <div className="mt-1 whitespace-pre-wrap">{incident.resolutionNote}</div>
        </div>
      ) : null}
      {actionError ? <div className="rounded-md border border-destructive px-3 py-2 text-sm text-destructive">{(actionError as ApiError).message}</div> : null}
      <div className="grid gap-4 lg:grid-cols-2">
        <div className="space-y-3 rounded-md border p-3">
          <div className="text-sm font-medium">{t("incidents:workflow")}</div>
          <div className="flex flex-wrap gap-2">
            {!incident.acknowledgedAt ? (
              <Button type="button" variant="secondary" disabled={busy} onClick={() => void mutations.acknowledge.mutateAsync()}>
                <CheckCircle2 className="h-4 w-4" />
                {t("incidents:acknowledge")}
              </Button>
            ) : null}
            {incident.status === "resolved" ? (
              <Button type="button" variant="secondary" disabled={busy} onClick={() => void mutations.reopen.mutateAsync()}>
                <Undo2 className="h-4 w-4" />
                {t("incidents:reopen")}
              </Button>
            ) : null}
          </div>
          {incident.status === "open" ? (
            <form className="space-y-2" onSubmit={submitResolve}>
              <label className="space-y-1 text-xs uppercase text-muted-foreground">
                <span>{t("incidents:resolutionNote")}</span>
                <textarea
                  className="min-h-20 w-full resize-y rounded-md border bg-input px-3 py-2 text-sm normal-case text-foreground outline-none focus:ring-2 focus:ring-ring"
                  value={resolutionNote}
                  onChange={(event) => setResolutionNote(event.target.value)}
                  maxLength={1000}
                  placeholder={t("incidents:resolutionPlaceholder")}
                  aria-label={t("incidents:resolutionNote")}
                />
              </label>
              <Button type="submit" variant="primary" disabled={busy}>
                <CheckCircle2 className="h-4 w-4" />
                {t("incidents:resolve")}
              </Button>
            </form>
          ) : null}
        </div>
        <div className="space-y-3 rounded-md border p-3">
          <div className="text-sm font-medium">{t("incidents:comments")}</div>
          <form className="space-y-2" onSubmit={submitComment}>
            <textarea
              className="min-h-20 w-full resize-y rounded-md border bg-input px-3 py-2 text-sm text-foreground outline-none focus:ring-2 focus:ring-ring"
              value={commentBody}
              onChange={(event) => setCommentBody(event.target.value)}
              maxLength={2000}
              placeholder={t("incidents:commentPlaceholder")}
              aria-label={t("incidents:comments")}
            />
            <Button type="submit" variant="secondary" disabled={busy || !commentBody.trim()}>
              <MessageSquare className="h-4 w-4" />
              {t("incidents:addComment")}
            </Button>
          </form>
          <IncidentComments comments={comments} />
        </div>
      </div>
    </div>
  );
}

function IncidentComments({ comments }: { comments: IncidentComment[] }) {
  const { t } = useTranslation("incidents");
  if (comments.length === 0) {
    return <div className="rounded-md border bg-muted px-3 py-2 text-sm text-muted-foreground">{t("noComments")}</div>;
  }
  return (
    <div className="space-y-2">
      {comments.map((comment) => (
        <div key={comment.id} className="rounded-md border px-3 py-2 text-sm">
          <div className="flex flex-wrap items-center justify-between gap-2 text-xs text-muted-foreground">
            <span>{comment.author}</span>
            <span>{dateTime(comment.createdAt)}</span>
          </div>
          <div className="mt-1 whitespace-pre-wrap">{comment.body}</div>
        </div>
      ))}
    </div>
  );
}

type LogSearchFormState = {
  appId: string;
  severity: string;
  eventName: string;
  softwareVersion: string;
  text: string;
  sinceHours: string;
  limit: string;
};

const defaultLogSearchForm: LogSearchFormState = {
  appId: "",
  severity: "error",
  eventName: "",
  softwareVersion: "",
  text: "",
  sinceHours: "24",
  limit: "25"
};

function LogSearchPanel({ scope }: { scope: { clientId: number; stationId?: string } }) {
  const { t } = useTranslation(["incidents", "common"]);
  const [draft, setDraft] = useState<LogSearchFormState>(defaultLogSearchForm);
  const [applied, setApplied] = useState<LogSearchFormState>(defaultLogSearchForm);
  const search = useLogSearch(logSearchInput(scope, applied));
  const updateDraft = (field: keyof LogSearchFormState, value: string) => {
    setDraft((current) => ({ ...current, [field]: value }));
  };
  const submit = (event: FormEvent) => {
    event.preventDefault();
    setApplied(draft);
  };
  const reset = () => {
    setDraft(defaultLogSearchForm);
    setApplied(defaultLogSearchForm);
  };

  return (
    <div className="space-y-4">
      <form className="grid gap-3 md:grid-cols-2 xl:grid-cols-4" onSubmit={submit}>
        <label className="space-y-1 text-xs uppercase text-muted-foreground">
          <span>{t("common:fields.severity")}</span>
          <select
            className="h-9 w-full rounded-md border bg-input px-2 text-sm normal-case text-foreground outline-none focus:ring-2 focus:ring-ring"
            value={draft.severity}
            onChange={(event) => updateDraft("severity", event.target.value)}
            aria-label={t("common:fields.severity")}
          >
            <option value="">{t("common:state.any")}</option>
            <option value="debug">debug</option>
            <option value="info">info</option>
            <option value="warn">warn</option>
            <option value="error">error</option>
          </select>
        </label>
        <LogSearchInputField label={t("common:fields.app")} value={draft.appId} onChange={(value) => updateDraft("appId", value)} placeholder="mailarchiver" />
        <LogSearchInputField label={t("common:fields.event")} value={draft.eventName} onChange={(value) => updateDraft("eventName", value)} placeholder="backup.failed" />
        <LogSearchInputField label={t("common:fields.version")} value={draft.softwareVersion} onChange={(value) => updateDraft("softwareVersion", value)} placeholder="1.2.14" />
        <label className="space-y-1 text-xs uppercase text-muted-foreground">
          <span>{t("common:fields.since")}</span>
          <select
            className="h-9 w-full rounded-md border bg-input px-2 text-sm normal-case text-foreground outline-none focus:ring-2 focus:ring-ring"
            value={draft.sinceHours}
            onChange={(event) => updateDraft("sinceHours", event.target.value)}
            aria-label={t("common:fields.since")}
          >
            <option value="1">1h</option>
            <option value="6">6h</option>
            <option value="24">24h</option>
            <option value="72">72h</option>
            <option value="168">7d</option>
          </select>
        </label>
        <label className="space-y-1 text-xs uppercase text-muted-foreground">
          <span>{t("common:fields.limit")}</span>
          <select
            className="h-9 w-full rounded-md border bg-input px-2 text-sm normal-case text-foreground outline-none focus:ring-2 focus:ring-ring"
            value={draft.limit}
            onChange={(event) => updateDraft("limit", event.target.value)}
            aria-label={t("common:fields.limit")}
          >
            <option value="25">25</option>
            <option value="50">50</option>
            <option value="100">100</option>
            <option value="200">200</option>
          </select>
        </label>
        <label className="space-y-1 text-xs uppercase text-muted-foreground md:col-span-2">
          <span>{t("common:fields.text")}</span>
          <input
            className="h-9 w-full rounded-md border bg-input px-3 text-sm normal-case text-foreground outline-none focus:ring-2 focus:ring-ring"
            value={draft.text}
            onChange={(event) => updateDraft("text", event.target.value)}
            placeholder={t("incidents:textPlaceholder")}
            aria-label={t("common:fields.text")}
          />
        </label>
        <div className="flex flex-wrap items-end gap-2 xl:col-span-4">
          <Button type="submit" variant="primary">
            <Search className="h-4 w-4" />
            {t("common:actions.apply")}
          </Button>
          <Button type="button" variant="secondary" onClick={() => void search.refetch()}>
            <RefreshCw className="h-4 w-4" />
            {t("common:actions.refresh")}
          </Button>
          <Button type="button" variant="ghost" onClick={reset}>
            {t("common:actions.reset")}
          </Button>
        </div>
      </form>
      <DataState error={search.error} loading={search.isLoading}>
        <LogTable entries={search.data?.entries ?? []} emptyTitle={t("incidents:noLogs")} />
      </DataState>
    </div>
  );
}

function LogSearchInputField({ label, value, onChange, placeholder }: { label: string; value: string; onChange: (value: string) => void; placeholder: string }) {
  return (
    <label className="space-y-1 text-xs uppercase text-muted-foreground">
      <span>{label}</span>
      <input
        className="h-9 w-full rounded-md border bg-input px-3 text-sm normal-case text-foreground outline-none focus:ring-2 focus:ring-ring"
        value={value}
        onChange={(event) => onChange(event.target.value)}
        placeholder={placeholder}
        aria-label={label}
      />
    </label>
  );
}

function logSearchInput(scope: { clientId: number; stationId?: string }, form: LogSearchFormState): LogSearchInput {
  const input: LogSearchInput = {
    clientId: scope.clientId,
    sinceHours: Number(form.sinceHours),
    limit: Number(form.limit)
  };
  if (scope.stationId) {
    input.stationId = scope.stationId;
  }
  const appId = form.appId.trim();
  if (appId) {
    input.appId = appId;
  }
  if (form.severity) {
    input.severity = form.severity;
  }
  const eventName = form.eventName.trim();
  if (eventName) {
    input.eventName = eventName;
  }
  const softwareVersion = form.softwareVersion.trim();
  if (softwareVersion) {
    input.softwareVersion = softwareVersion;
  }
  const text = form.text.trim();
  if (text) {
    input.text = text;
  }
  return input;
}

function LogTable({ entries, emptyTitle }: { entries: LogEntry[]; emptyTitle?: string }) {
  const { t } = useTranslation(["incidents", "common"]);
  if (entries.length === 0) {
    return <EmptyState title={emptyTitle || t("incidents:noLatestErrors")} />;
  }
  return (
    <div className="overflow-x-auto">
      <table className="w-full min-w-[860px] text-left text-sm">
        <thead className="bg-muted text-xs uppercase text-muted-foreground">
          <tr>
            <th className="px-3 py-2">{t("common:fields.time")}</th>
            <th className="px-3 py-2">{t("common:fields.station")}</th>
            <th className="px-3 py-2">{t("common:fields.event")}</th>
            <th className="px-3 py-2">{t("common:fields.message")}</th>
            <th className="px-3 py-2">{t("common:fields.version")}</th>
          </tr>
        </thead>
        <tbody>
          {entries.map((entry, index) => (
            <tr key={`${entry.stationId}-${entry.time}-${entry.eventName}-${index}`} className="border-t align-top">
              <td className="whitespace-nowrap px-3 py-2 text-muted-foreground">{dateTime(entry.time)}</td>
              <td className="px-3 py-2">{entry.hostname || entry.stationId}</td>
              <td className="px-3 py-2">
                <div>{entry.eventName}</div>
                <div className="mt-1">
                  <Badge tone={entry.severity === "error" ? "danger" : entry.severity === "warn" ? "warning" : "neutral"}>{entry.severity}</Badge>
                </div>
              </td>
              <td className="px-3 py-2">{entry.message}</td>
              <td className="px-3 py-2 text-muted-foreground">{entry.softwareVersion}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function IngestionHealthPanel({ data }: { data: IngestionHealth }) {
  const { t } = useTranslation("common");
  const acceptedPercent = data.totalBatches > 0 ? (data.acceptedBatches / data.totalBatches) * 100 : undefined;
  return (
    <div className="space-y-6">
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <MetricCard icon={<Activity className="h-4 w-4" />} label={t("fields.batches")} value={formatNumber(data.totalBatches)} />
        <MetricCard icon={<RefreshCw className="h-4 w-4" />} label={t("fields.accepted")} value={formatPercent(acceptedPercent)} />
        <MetricCard icon={<AlertTriangle className="h-4 w-4" />} label={t("fields.rejected")} value={formatNumber(data.rejectedBatches)} />
        <MetricCard icon={<HardDrive className="h-4 w-4" />} label={t("fields.payload")} value={formatBytes(data.totalPayloadBytes)} />
      </div>
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <Field label={t("fields.records")} value={formatNumber(data.totalRecords)} />
        <Field label={t("fields.clients")} value={formatNumber(data.distinctClients)} />
        <Field label={t("fields.stations")} value={formatNumber(data.distinctStations)} />
        <Field label={t("fields.lastBatch")} value={data.lastReceivedAt ? ago(data.lastReceivedAt) : t("state.none")} />
      </div>
      <Panel title="Recent Rejects">
        {data.recentRejects.length === 0 ? (
          <EmptyState title="No rejected batches" />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[860px] text-left text-sm">
              <thead className="bg-muted text-xs uppercase text-muted-foreground">
                <tr>
                  <th className="px-3 py-2">Received</th>
                  <th className="px-3 py-2">Batch</th>
                  <th className="px-3 py-2">Client</th>
                  <th className="px-3 py-2">Station</th>
                  <th className="px-3 py-2">Reason</th>
                  <th className="px-3 py-2">Records</th>
                </tr>
              </thead>
              <tbody>
                {data.recentRejects.map((reject) => (
                  <tr key={reject.batchId} className="border-t align-top">
                    <td className="whitespace-nowrap px-3 py-2 text-muted-foreground">{dateTime(reject.receivedAt)}</td>
                    <td className="px-3 py-2 font-medium">{reject.batchId}</td>
                    <td className="px-3 py-2">{reject.clientId}</td>
                    <td className="px-3 py-2">{reject.stationId}</td>
                    <td className="px-3 py-2">{reject.rejectReason}</td>
                    <td className="px-3 py-2">{reject.recordCount}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Panel>
    </div>
  );
}

function AuditEventsTable({ events }: { events: AuditEvent[] }) {
  const { t } = useTranslation(["audit", "common"]);
  if (events.length === 0) {
    return <EmptyState title={t("audit:empty")} />;
  }
  return (
    <div className="overflow-x-auto rounded-md border bg-panel">
      <table className="w-full min-w-[980px] text-left text-sm">
        <thead className="bg-muted text-xs uppercase text-muted-foreground">
          <tr>
            <th className="px-3 py-2">{t("common:fields.time")}</th>
            <th className="px-3 py-2">{t("common:fields.event")}</th>
            <th className="px-3 py-2">{t("common:fields.actor")}</th>
            <th className="px-3 py-2">{t("common:fields.scope")}</th>
            <th className="px-3 py-2">{t("common:fields.request")}</th>
            <th className="px-3 py-2">{t("common:fields.metadata")}</th>
          </tr>
        </thead>
        <tbody>
          {events.map((event) => (
            <tr key={event.id} className="border-t align-top">
              <td className="whitespace-nowrap px-3 py-2 text-muted-foreground">{dateTime(event.createdAt)}</td>
              <td className="px-3 py-2">
                <Badge tone={event.eventType.includes("denied") ? "danger" : "neutral"}>{event.eventType}</Badge>
              </td>
              <td className="px-3 py-2">{event.actor || "unknown"}</td>
              <td className="px-3 py-2 text-muted-foreground">
                {[event.appId, event.clientId ? `client ${event.clientId}` : "", event.stationId].filter(Boolean).join(" / ") || t("common:state.global")}
              </td>
              <td className="px-3 py-2 text-muted-foreground">{event.requestId}</td>
              <td className="max-w-[360px] px-3 py-2">
                <code className="block truncate rounded-sm bg-muted px-2 py-1 text-xs">{metadataSummary(event.metadata)}</code>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function HostOverviewPanel({ data }: { data: HostOverview }) {
  return (
    <div className="space-y-4">
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <MetricCard icon={<Server className="h-4 w-4" />} label="Load 1m" value={formatNumber(data.load1)} />
        <MetricCard icon={<Cpu className="h-4 w-4" />} label="Memory Used" value={data.memory ? formatPercent(data.memory.usedPercent) : "none"} />
        <MetricCard icon={<Monitor className="h-4 w-4" />} label="Available Memory" value={data.memory ? formatBytes(data.memory.availableBytes) : "none"} />
        <MetricCard icon={<RefreshCw className="h-4 w-4" />} label="Uptime" value={formatDuration(data.uptimeSeconds)} />
      </div>
      <div className="space-y-2">
        <div className="text-xs uppercase text-muted-foreground">Exporters</div>
        {data.exporters.length === 0 ? (
          <EmptyState title="No exporter status" />
        ) : (
          <div className="flex flex-wrap gap-2">
            {data.exporters.map((exporter) => (
              <Badge key={exporter.job} tone={exporter.up ? "success" : "danger"}>
                {`${exporter.job}: ${exporter.up ? "up" : "down"}`}
              </Badge>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function SignalFamiliesPanel({ data }: { data: SignalFamilies }) {
  if (data.families.length === 0) {
    return <EmptyState title="No signal families" />;
  }
  return (
    <div className="space-y-2">
      {data.families.map((family) => (
        <div key={family.familyId} className="rounded-md border p-3">
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <div className="truncate text-sm font-medium">{family.familyId}</div>
              <div className="truncate text-xs text-muted-foreground">
                {family.kind} · {family.producer} · {family.defaultCadence}
              </div>
            </div>
            <Badge tone={family.available ? "success" : "neutral"}>{family.available ? "available" : "not granted"}</Badge>
          </div>
          <div className="mt-2 flex flex-wrap gap-2 text-xs text-muted-foreground">
            <span>{family.retentionClass}</span>
            <span>{family.visibility}</span>
            {family.licenseStatus ? <span>{family.licenseStatus}</span> : null}
            {family.expiresAt ? <span>expires {dateTime(family.expiresAt)}</span> : null}
          </div>
        </div>
      ))}
    </div>
  );
}

function DisksPanel({ data }: { data: HostDisks }) {
  return (
    <div className="space-y-4">
      <div className="space-y-2">
        <div className="flex items-center gap-2 text-sm font-medium">
          <HardDrive className="h-4 w-4 text-primary" />
          Filesystems
        </div>
        {data.filesystems.length === 0 ? (
          <EmptyState title="No filesystem data" />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[560px] text-left text-sm">
              <thead className="bg-muted text-xs uppercase text-muted-foreground">
                <tr>
                  <th className="px-3 py-2">Mount</th>
                  <th className="px-3 py-2">Device</th>
                  <th className="px-3 py-2">Used</th>
                  <th className="px-3 py-2">Available</th>
                </tr>
              </thead>
              <tbody>
                {data.filesystems.map((fs) => (
                  <tr key={`${fs.device}-${fs.mountpoint}`} className="border-t">
                    <td className="px-3 py-2 font-medium">{fs.mountpoint || "none"}</td>
                    <td className="px-3 py-2 text-muted-foreground">{fs.device}</td>
                    <td className="px-3 py-2">{formatPercent(fs.usedPercent)}</td>
                    <td className="px-3 py-2">{formatBytes(fs.availableBytes)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
      <div className="space-y-2">
        <div className="text-sm font-medium">SMART</div>
        {data.smartDevices.length === 0 ? (
          <EmptyState title="No SMART data" />
        ) : (
          <div className="grid gap-3 md:grid-cols-2">
            {data.smartDevices.map((device) => (
              <div key={device.device} className="rounded-md border p-3">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="truncate font-medium">{device.device}</div>
                    <div className="truncate text-xs text-muted-foreground">{device.model || "unknown model"}</div>
                  </div>
                  <Badge tone={device.healthy === false ? "danger" : "success"}>{device.healthy === false ? "bad" : "ok"}</Badge>
                </div>
                <dl className="mt-3 grid grid-cols-2 gap-3 text-sm">
                  <Field label="Temp" value={temperature(device.temperatureCelsius)} />
                  <Field label="Used" value={formatPercent(device.percentageUsed)} />
                  <Field label="Reallocated" value={formatNumber(device.reallocatedSectors)} />
                  <Field label="Pending" value={formatNumber(device.pendingSectors)} />
                </dl>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function ProcessesPanel({ data }: { data: HostProcesses }) {
  if (data.processes.length === 0) {
    return <EmptyState title="No process pressure" />;
  }
  return (
    <div className="overflow-x-auto">
      <table className="w-full min-w-[680px] text-left text-sm">
        <thead className="bg-muted text-xs uppercase text-muted-foreground">
          <tr>
            <th className="px-3 py-2">Group</th>
            <th className="px-3 py-2">CPU/s</th>
            <th className="px-3 py-2">RSS</th>
            <th className="px-3 py-2">Proc</th>
            <th className="px-3 py-2">Threads</th>
            <th className="px-3 py-2">Read/s</th>
            <th className="px-3 py-2">Write/s</th>
          </tr>
        </thead>
        <tbody>
          {data.processes.map((process) => (
            <tr key={process.groupName} className="border-t">
              <td className="px-3 py-2 font-medium">{process.groupName}</td>
              <td className="px-3 py-2">{formatNumber(process.cpuSecondsPerSec)}</td>
              <td className="px-3 py-2">{formatBytes(process.residentBytes)}</td>
              <td className="px-3 py-2">{formatNumber(process.processCount)}</td>
              <td className="px-3 py-2">{formatNumber(process.threadCount)}</td>
              <td className="px-3 py-2">{formatBytes(process.readBytesPerSec)}</td>
              <td className="px-3 py-2">{formatBytes(process.writeBytesPerSec)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function ContainersPanel({ data }: { data: HostContainers }) {
  if (data.containers.length === 0) {
    return <EmptyState title="No containers" />;
  }
  return (
    <div className="space-y-3">
      {data.containers.map((container) => (
        <div key={container.id} className="rounded-md border p-3">
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <div className="flex items-center gap-2">
                <Boxes className="h-4 w-4 text-primary" />
                <span className="truncate font-medium">{container.name || container.id}</span>
              </div>
              <div className="mt-1 truncate text-xs text-muted-foreground">{container.image || container.composeService || container.id}</div>
            </div>
            <Badge tone={container.present ? "success" : "danger"}>{container.present ? "present" : "missing"}</Badge>
          </div>
          <dl className="mt-3 grid grid-cols-2 gap-3 text-sm md:grid-cols-4">
            <Field label="CPU/s" value={formatNumber(container.cpuSecondsPerSec)} />
            <Field label="RSS" value={formatBytes(container.memoryRssBytes)} />
            <Field label="Cache" value={formatBytes(container.memoryCacheBytes)} />
            <Field label="Started" value={container.startTime ? ago(container.startTime) : "none"} />
          </dl>
        </div>
      ))}
    </div>
  );
}

function CorrelationPanel({ data }: { data: HostCorrelation }) {
  return (
    <div className="space-y-3">
      <div className="grid grid-cols-2 gap-3 text-sm">
        <Field label="Load" value={formatNumber(data.host.load1)} />
        <Field label="Generated" value={dateTime(data.generatedAt)} />
      </div>
      <LogTable entries={data.logs.entries} />
    </div>
  );
}

function MetricCard({ icon, label, value }: { icon: ReactNode; label: string; value: string }) {
  return (
    <div className="rounded-md border p-3">
      <div className="flex items-center gap-2 text-xs uppercase text-muted-foreground">
        {icon}
        {label}
      </div>
      <div className="mt-2 truncate text-lg font-semibold">{value}</div>
    </div>
  );
}

const debugWindowSchema = z.object({
  durationMinutes: z.coerce.number().int().min(5, "Minimum is 5 minutes").max(120, "Maximum is 120 minutes"),
  reason: z.string().trim().min(3, "Reason is required").max(200, "Reason is too long")
});

type DebugWindowInput = z.input<typeof debugWindowSchema>;
type DebugWindowOutput = z.output<typeof debugWindowSchema>;

function StationDebugWindow({ stationId, clientId, compact = false }: { stationId: string; clientId: number; compact?: boolean }) {
  const { t } = useTranslation(["stations", "common"]);
  const [open, setOpen] = useState(!compact);
  const mutations = useDebugWindowMutations(stationId, clientId);
  const form = useForm<DebugWindowInput, unknown, DebugWindowOutput>({
    resolver: zodResolver(debugWindowSchema),
    defaultValues: {
      durationMinutes: 30,
      reason: "support-debug-window"
    }
  });
  const mutationError = mutations.create.error || mutations.revoke.error;
  const errorMessage =
    form.formState.errors.reason?.message ||
    form.formState.errors.durationMinutes?.message ||
    (mutationError instanceof Error ? mutationError.message : "");
  const submit = form.handleSubmit((values) => {
    mutations.create.mutate(values, {
      onSuccess: () => {
        form.reset({ durationMinutes: 30, reason: "support-debug-window" });
        setOpen(!compact);
      }
    });
  });

  if (compact && !open) {
    return (
      <Button
        variant="secondary"
        onClick={() => setOpen(true)}
        title={t("stations:startDebugWindow")}
      >
        <AlertTriangle className="h-4 w-4" />
        {t("stations:debug")}
      </Button>
    );
  }

  return (
    <form className="min-w-[260px] space-y-2" onSubmit={submit}>
      <div className="flex gap-2">
        <select
          className="h-9 w-24 rounded-md border bg-input px-2 text-sm outline-none focus:ring-2 focus:ring-ring"
          {...form.register("durationMinutes")}
          aria-label={t("stations:durationLabel")}
        >
          <option value="15">15m</option>
          <option value="30">30m</option>
          <option value="60">60m</option>
          <option value="120">120m</option>
        </select>
        <input
          className="h-9 min-w-0 flex-1 rounded-md border bg-input px-3 text-sm outline-none focus:ring-2 focus:ring-ring"
          {...form.register("reason")}
          placeholder={t("stations:reason")}
        />
      </div>
      {errorMessage ? <div className="text-xs text-destructive">{errorMessage}</div> : null}
      <div className="flex flex-wrap gap-2">
        <Button type="submit" disabled={mutations.create.isPending}>
          <Monitor className="h-4 w-4" />
          {t("common:actions.start")}
        </Button>
        <Button type="button" variant="danger" disabled={mutations.revoke.isPending} onClick={() => mutations.revoke.mutate()}>
          {t("stations:revoke")}
        </Button>
        {compact ? (
          <Button type="button" variant="ghost" onClick={() => setOpen(false)}>
            {t("common:actions.cancel")}
          </Button>
        ) : null}
      </div>
    </form>
  );
}

function DataState({ loading, error, children }: { loading: boolean; error: Error | null; children: ReactNode }) {
  const { t } = useTranslation("common");
  if (loading) {
    return <div className="rounded-md border bg-panel px-4 py-6 text-sm text-muted-foreground">{t("state.loading")}</div>;
  }
  if (error) {
    const apiError = error as ApiError;
    if (apiError.status === 403) {
      return (
        <div className="rounded-md border border-warning bg-panel px-4 py-6 text-sm text-warning">
          {t("state.noAccessScope")}
        </div>
      );
    }
    return <div className="rounded-md border border-destructive bg-panel px-4 py-6 text-sm text-destructive">{apiError.message}</div>;
  }
  return <>{children}</>;
}

function Panel({ title, children }: { title: string; children: ReactNode }) {
  return (
    <section className="min-w-0 overflow-hidden rounded-md border bg-panel">
      <div className="border-b px-4 py-3">
        <h2 className="text-sm font-semibold">{title}</h2>
      </div>
      <div className="p-4">{children}</div>
    </section>
  );
}

function PageTitle({ title, action }: { title: string; action?: ReactNode }) {
  return (
    <div className="flex min-h-10 items-center justify-between gap-3">
      <h1 className="truncate text-2xl font-semibold">{title}</h1>
      {action}
    </div>
  );
}

function RefreshButton({ onClick }: { onClick: () => void }) {
  const { t } = useTranslation("common");
  return (
    <Button variant="secondary" onClick={onClick} title={t("actions.refresh")}>
      <RefreshCw className="h-4 w-4" />
    </Button>
  );
}

function Field({ label, value }: { label: string; value: string }) {
  return (
    <div className="min-w-0">
      <dt className="text-xs uppercase text-muted-foreground">{label}</dt>
      <dd className="truncate font-medium">{value || "none"}</dd>
    </div>
  );
}

function metadataSummary(metadata: Record<string, unknown>) {
  const text = JSON.stringify(metadata ?? {});
  if (text.length <= 180) {
    return text;
  }
  return `${text.slice(0, 177)}...`;
}

function incidentStatusTone(status: string): "neutral" | "success" | "warning" | "danger" {
  return status === "resolved" ? "success" : "danger";
}

function incidentSeverityTone(severity: string): "neutral" | "success" | "warning" | "danger" {
  if (severity === "error") {
    return "danger";
  }
  if (severity === "warn") {
    return "warning";
  }
  return "neutral";
}

function formatNumber(value: number | undefined) {
  if (value === undefined || !Number.isFinite(value)) {
    return "none";
  }
  return value.toLocaleString("en-US", { maximumFractionDigits: 2 });
}

function formatPercent(value: number | undefined) {
  if (value === undefined || !Number.isFinite(value)) {
    return "none";
  }
  return `${value.toLocaleString("en-US", { maximumFractionDigits: 1 })}%`;
}

function temperature(value: number | undefined) {
  if (value === undefined || !Number.isFinite(value)) {
    return "none";
  }
  return `${value.toLocaleString("en-US", { maximumFractionDigits: 1 })} C`;
}

function formatBytes(value: number | undefined) {
  if (value === undefined || !Number.isFinite(value)) {
    return "none";
  }
  const units = ["B", "KiB", "MiB", "GiB", "TiB"];
  let scaled = value;
  let unitIndex = 0;
  while (Math.abs(scaled) >= 1024 && unitIndex < units.length - 1) {
    scaled /= 1024;
    unitIndex += 1;
  }
  return `${scaled.toLocaleString("en-US", { maximumFractionDigits: unitIndex === 0 ? 0 : 1 })} ${units[unitIndex]}`;
}

function formatDuration(value: number | undefined) {
  if (value === undefined || !Number.isFinite(value)) {
    return "none";
  }
  const totalMinutes = Math.floor(value / 60);
  const days = Math.floor(totalMinutes / 1440);
  const hours = Math.floor((totalMinutes % 1440) / 60);
  const minutes = totalMinutes % 60;
  if (days > 0) {
    return `${days}d ${hours}h`;
  }
  if (hours > 0) {
    return `${hours}h ${minutes}m`;
  }
  return `${minutes}m`;
}
